'use client'

import { useMemo } from 'react'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { Input } from '@cocoonspace/ui/components/input'
import { useLingui } from '@lingui/react/macro'
import { parseAsString, useQueryStates } from 'nuqs'

interface Post {
	categories: {
		id: string
		name: string
	}[]
	title: string
}

interface PageSidebarProps {
	posts: Post[]
}

const normalizeString = (str: string) =>
	str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-zA-Z0-9]/g, '')
		.toLowerCase()

export const useFilterPosts = <T extends Post>(posts: T[]) => {
	const { filters } = useBlogFilters()

	return useMemo(() => {
		if (!posts) return []

		return posts
			.filter((post) => {
				if (!filters.search) return true

				const normalizedTitle = normalizeString(post.title)
				const normalizedSearch = normalizeString(filters.search)

				return normalizedTitle.includes(normalizedSearch)
			})
			.filter((post) => {
				if (!filters.category) return true

				return post.categories.some(({ name }) => name === filters.category)
			})
	}, [posts, filters])
}

export const useBlogFilters = () => {
	const [filters, setFilters] = useQueryStates({
		category: parseAsString,
		search: parseAsString,
	})

	return useMemo(() => ({ filters, setFilters }), [filters, setFilters])
}

export const PageSidebar = ({ posts }: PageSidebarProps) => {
	const { t } = useLingui()
	const { filters, setFilters } = useBlogFilters()

	const categories = useMemo(() => {
		if (!posts) {
			return []
		}

		const categories = new Map()

		for (const post of posts) {
			for (const category of post.categories) {
				if (!categories.has(category.id)) {
					categories.set(category.id, category)
				}
			}
		}

		return Array.from(categories.values())
	}, [posts])

	return (
		<Card className='sticky top-[calc(var(--app-navbar-height)+20px)] flex flex-col gap-4 p-4'>
			<div>
				<h2 className='text-lg font-bold'>{t`Rechercher`}</h2>

				<div className='flex flex-wrap items-start gap-2'>
					<Input
						aria-label={t`Rechercher un article`}
						onChange={(e) => setFilters({ search: e.target.value || null })}
						placeholder={t`Tapez votre recherche`}
						value={filters.search ?? ''}
					/>
				</div>
			</div>

			{!!categories.length && (
				<div>
					<h2 className='text-lg font-bold'>{t`Catégories`}</h2>

					<div className='flex flex-wrap items-start gap-2'>
						{categories.map(({ name, id }) => (
							<Button
								key={id}
								onClick={() => setFilters({ category: name })}
								size='sm'
								variant='ghost'
							>
								# {name}
							</Button>
						))}
					</div>
				</div>
			)}
		</Card>
	)
}
